import { joiResolver } from '@hookform/resolvers/joi';
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Loader from "../../components/Loader/Loader";
import { formatPhoneNumber } from "../../constants/formatPhoneNumber";
import { getHttpErrorMessageByStatus } from "../../constants/getHttpErrorMessageByStatus";
import logo from '../../constants/images/logo.svg';
import { linkStyles } from "../../constants/linkStyles";
import { MAIN_ROUTES } from "../../routing/main_routes";
import { userService } from "../../services/user.service";
import { userDetailsActions } from "../../store/slices/userDetailsSlice";
import { registrationCustomerValidator } from "../../validators/registrationCustomer.validator";
import styles from './RegistrationPageCustomer.module.scss';

const RegistrationPageCustomer = () => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const refId = searchParams.get('ref_id');

    const navigate = useNavigate();

    useEffect(() => {
        if (refId) {
            setValue("invited_by", refId);
        }
    }, []);

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
        reset,
      setValue
    } = useForm({
        resolver: joiResolver(registrationCustomerValidator),
        mode: 'all',
    });

    const onSubmit = async (data) => {
        setIsLoading(true);

        const user = {
            ...data,
            mobile_nummer: formatPhoneNumber(data.mobile_nummer),
        };

        // create user account
        try {
            await userService.create(user);
        } catch (error) {
            const errorMessage = getHttpErrorMessageByStatus(error);
            setError(errorMessage);
            setIsLoading(false);
            return;
        }

        // login
        try {
            const {data: tokens} = await userService.login(data);
            const {cleaner_id, user_id, iat, exp} = jwtDecode(tokens.access);

            if (!cleaner_id) {
                await dispatch(userDetailsActions.getById({id: user_id}));
                navigate(MAIN_ROUTES.MAIN, {state: {id: user_id}});
                localStorage.setItem('isCleaner', 'false');
            }

            localStorage.setItem('token', JSON.stringify(tokens));
            localStorage.setItem('expires', JSON.stringify({iat, exp}));
        } catch (error) {
            const errorMessage = getHttpErrorMessageByStatus(error);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }

        reset();
    }

    const handleClearError = () => {
        if (error) {
            setError(null);
        }
    }



    return (
        isLoading ?
            <Loader/> :
            <div className={styles.registrationPage_wrapper}>
                <div className={styles.registrationPage_wrapper_registration}>
                    <div className={styles.registrationPage_wrapper_registration_title}>
                        <img src={logo} alt="quality cleaning logo"/>

                        <div>
                            <h1 className={styles.title}>Створити Аккаунт</h1>
                            <p className={styles.paragraph}>Вітаємо у Quality Cleaning Ukraine!</p>
                        </div>
                    </div>

                    <form
                        onChange={handleClearError}
                        onSubmit={handleSubmit(onSubmit)}
                        className={styles.registrationPage_wrapper_registration_form}
                        action="#"
                    >
                        <div className={styles.registrationPage_wrapper_registration_form_content}>
                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Прізвище
                                    <input
                                        {...register('last_name')}
                                        className={styles.input}
                                        type="text"
                                        placeholder={'Введіть ваше прізвище'}
                                        autoComplete='new_last_name'
                                    />
                                </label>
                                {errors.last_name && <span className={styles.error}> {errors.last_name.message} </span>}
                            </div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Ім'я
                                    <input
                                        {...register('first_name')}
                                        className={styles.input}
                                        type="text"
                                        placeholder={'Введіть ваше ім’я'}
                                         autoComplete='new_first_name'
                                    />
                                </label>
                                {errors.first_name &&
                                    <span className={styles.error}> {errors.first_name.message} </span>}
                            </div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Email
                                    <input
                                        {...register('email')}
                                        className={styles.input}
                                        type="email"
                                        placeholder={'Введіть ваш email'}
                                        autoComplete='new_email'
                                    />
                                </label>
                                {errors.email && <span className={styles.error}> {errors.email.message} </span>}</div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Номер телефону
                                    <InputMask
                                        {...register('mobile_nummer')}
                                        className={styles.input}
                                        mask="+38 (099) 999-99-99"
                                        placeholder="+38 (0XX) XXX-XX-XX"
                                        autoComplete='new_mobile_nummer'
                                    />
                                </label>
                                {errors.mobile_nummer &&
                                    <span className={styles.error}> {errors.mobile_nummer.message} </span>}
                            </div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Пароль
                                    <input
                                        {...register('password')}
                                        className={styles.input}
                                        type="password"
                                        autoComplete='new_password'
                                    />
                                </label>
                                {errors.password && <span className={styles.error}> {errors.password.message} </span>}
                            </div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Повторити пароль
                                    <input
                                        {...register('confirm_password')}
                                        className={styles.input}
                                        type="password"
                                        autoComplete='new_confirm_password'
                                    />
                                </label>
                                {errors.confirm_password &&
                                    <span className={styles.error}> {errors.confirm_password.message} </span>}
                            </div>

                            <div className={styles.registrationPage_wrapper_registration_form_content_item}>
                                <label>
                                    Реферальний ID
                                    <input
                                      disabled={!!refId}
                                      {...register('invited_by')}
                                      className={styles.input}
                                      placeholder={"Реферальний ID"}
                                        type="text"
                                        autoComplete='new_invited_by'
                                    />
                                </label>
                                {errors.invited_by &&
                                  <span className={styles.error}> {errors.invited_by.message} </span>}
                                {error && <span className={styles.error}> {error} </span>}
                            </div>
                        </div>

                        <button
                            disabled={!isValid}
                            className={styles.registrationPage_wrapper_registration_form_button}
                        >Створити Аккаунт
                        </button>
                    </form>

                    <p className={styles.registrationPage_wrapper_registration_linkLogin}>
                        Вже маєте аккаунт?
                        <Link to={MAIN_ROUTES.LOGIN} style={linkStyles}> Увійти</Link>
                    </p>
                </div>
            </div>
    );
};

export default RegistrationPageCustomer;
